import { Plug, Droplet, Waves, ArrowRightLeft, Ruler } from 'lucide-react';
import FitPill from '../ui/FitPill';
import { formatAmps, formatCentsCompact, formatDimensions, formatFeet } from '../../lib/format';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import SpaceTypeIllustration from '../ui/SpaceTypeIllustration';

const STATUS_LABELS = {
  available: null,
  occupied: 'Occupied',
  reserved: 'Reserved',
  maintenance: 'Unavailable',
};

export default function UnitRow({ unit, isSelected, onSelect }) {
  const blocked = unit.fit?.status === 'incompatible';
  const rentable = unit.status === 'available' && !blocked;
  const statusLabel = STATUS_LABELS[unit.status];

  return (
    <li>
      <button
        type="button"
        onClick={() => rentable && onSelect(unit)}
        disabled={!rentable}
        aria-pressed={isSelected}
        className={`w-full rounded-xl border p-4 text-left transition ${
          isSelected ? 'border-brand-600 bg-brand-50/40 ring-2 ring-brand-500/30'
            : rentable ? 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-card'
            : 'border-slate-200 bg-slate-50'
        } ${rentable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      >
        <div className="flex items-start gap-4">
          {/*
            The same drawing the landing page uses, so a covered 45ft space
            looks like one on both screens. Drawn to the space's own length and
            clearance rather than being a stock icon per category: whether a rig
            clears the roof is the question this row exists to answer, and a
            generic "covered" glyph answers it for no one.

            Muted when the space cannot be taken, so the row reads as
            unavailable at a glance rather than only via its badge.
          */}
          <div className={`hidden h-16 w-24 shrink-0 rounded-lg bg-slate-50 p-1.5 sm:block ${
            rentable ? 'text-brand-600' : 'text-slate-400'}`}>
            <SpaceTypeIllustration spaceType={unit.space_type} lengthFt={unit.length_ft}
                                   clearanceFt={unit.height_clearance_ft} className="h-full w-full" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-extrabold text-slate-900">Unit {unit.unit_number}</span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                {SPACE_TYPE_LABELS[unit.space_type] ?? unit.space_type}
              </span>
              <FitPill status={unit.fit?.status} />
              {statusLabel && (
                <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                  {statusLabel}
                </span>
              )}
            </div>

            <p className={`mt-1.5 text-[12px] font-medium ${blocked ? 'text-rose-700' : 'text-slate-600'}`}>
              {unit.fit?.reasons?.[0] ?? `${formatDimensions(unit.length_ft, unit.width_ft)} space`}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
              <Spec Icon={Ruler} label={formatDimensions(unit.length_ft, unit.width_ft)} />
              {unit.height_clearance_ft && <Spec Icon={Ruler} label={`${formatFeet(unit.height_clearance_ft)} clearance`} />}
              <Spec Icon={ArrowRightLeft} label={unit.is_pull_through ? 'Pull-through' : 'Back-in'} />
              {unit.has_power && <Spec Icon={Plug} label={formatAmps(unit.electrical_amps)} />}
              {unit.has_water && <Spec Icon={Droplet} label="Water" />}
              {unit.has_sewer && <Spec Icon={Waves} label="Sewer" />}
            </div>

            {unit.notes && <p className="mt-2 text-[12px] italic text-slate-500">{unit.notes}</p>}
          </div>

          <div className="shrink-0 text-right">
            <p className="text-lg font-extrabold text-slate-900">{formatCentsCompact(unit.monthly_rate_cents)}</p>
            <p className="text-[12px] text-slate-500">/month</p>
            {unit.street_rate_cents && unit.street_rate_cents > unit.monthly_rate_cents && (
              <p className="text-[12px] text-slate-400 line-through">{formatCentsCompact(unit.street_rate_cents)}</p>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function Spec({ Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3 w-3 text-brand-500" aria-hidden="true" />
      {label}
    </span>
  );
}
